import { IProductSummaryResponse } from '../models/iProductSearchRequest';

/**
 * Opens Gemini search results in a new window with styled content
 * @param data - The product summary response data
 */
export const openGeminiResultsWindow = (data: IProductSummaryResponse): void => {
  const newWindow = window.open(
    '',
    '_blank',
    'width=800,height=700,scrollbars=yes,resizable=yes,location=no,menubar=no,toolbar=no,status=no'
  );

  if (!newWindow) {
    throw new Error('Unable to open new window. Please check your popup blocker settings.');
  }

  // Ensure window stays in foreground
  newWindow.focus();

  // Create clean, Material-UI inspired HTML content
  const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Gemini Product Summary - Brady Corporation</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: "Roboto", "Helvetica", "Arial", sans-serif;
          background-color: #f5f5f5;
          padding: 24px;
          line-height: 1.6;
        }
        
        .container {
          max-width: 800px;
          margin: 0 auto;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        
        .header {
          background-color: #1976d2;
          color: white;
          padding: 16px 24px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .header h1 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 500;
        }
        
        .content {
          padding: 24px;
        }
        
        .section {
          margin-bottom: 24px;
        }
        
        .section h2 {
          color: #1976d2;
          font-size: 1.125rem;
          font-weight: 500;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 2px solid #e3f2fd;
        }
        
        .summary-box {
          background: #f8f9fa;
          padding: 16px;
          border-radius: 4px;
          border-left: 4px solid #1976d2;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
        
        .links-list {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        
        .link-item {
          margin-bottom: 8px;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 4px;
          border: 1px solid #e0e0e0;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .link-item:hover {
          background: #e3f2fd;
        }
        
        .link {
          color: #1976d2;
          text-decoration: none;
          word-break: break-all;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .link:hover {
          text-decoration: underline;
        }
        
        .no-links {
          color: #666;
          font-style: italic;
          text-align: center;
          padding: 24px;
          background: #f8f9fa;
          border-radius: 4px;
          border: 1px solid #e0e0e0;
        }
        
        .close-btn {
          background: #1976d2;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          width: 100%;
          transition: background-color 0.2s;
          margin-top: 16px;
        }
        
        .close-btn:hover {
          background: #1565c0;
        }
        
        .timestamp {
          color: #666;
          font-size: 0.875rem;
          text-align: right;
          margin-top: 16px;
          padding-top: 8px;
          border-top: 1px solid #e0e0e0;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <span>🔍</span>
          <h1>Gemini Product Summary</h1>
        </div>
        
        <div class="content">
          <div class="section">
            <h2>Summary</h2>
            <div class="summary-box">${data.summary.replace(/\n/g, '<br>')}</div>
          </div>
          
          <div class="section">
            <h2>Related Links ${data.urls.length > 0 ? `(${data.urls.length})` : ''}</h2>
            ${
              data.urls.length > 0
                ? `
              <ul class="links-list">
                ${data.urls
                  .map(
                    (url) => `
                  <li class="link-item" onclick="window.open('${url}', '_blank', 'noopener,noreferrer')">
                    <div class="link">
                      <span>🔗</span>
                      <span>${url}</span>
                    </div>
                  </li>
                `
                  )
                  .join('')}
              </ul>
            `
                : `
              <div class="no-links">
                No related links were found for this request.
              </div>
            `
            }
          </div>
          
          <button class="close-btn" onclick="window.close()">
            Close Window
          </button>
          
          <div class="timestamp">
            Generated on ${new Date().toLocaleString()}
          </div>
        </div>
      </div>
    </body>
    </html>
  `;

  newWindow.document.write(htmlContent);
  newWindow.document.close();

  // Set up periodic focus to keep window in foreground
  const focusInterval = setInterval(() => {
    if (!newWindow.closed) {
      newWindow.focus();
    } else {
      clearInterval(focusInterval);
    }
  }, 100);

  // Stop the interval after 2 seconds
  setTimeout(() => {
    clearInterval(focusInterval);
  }, 2000);

  // Final focus after content is loaded
  setTimeout(() => {
    newWindow.focus();
  }, 100);
};

export default openGeminiResultsWindow;
